import { ToolbarEventManagerHelper } from './../../../services/event-managers/toolbar-event-manager.helper';
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BaseComponent } from '../../base/base.component';
import { ExternalLinkStore } from '../../../services/stores/external-link.store';
import { IntelliCatchReachabilityStore } from '../../../services/stores/intellicatch-reachability.store';
import { LoggerService } from '../../../services/logger.service';
import { AlertService } from '../../../services/alert.service';
import { NotificationType } from 'angular2-notifications';
import { isAiProviderUsable } from '../../../models/child-application/ai-provider.enum';
import { IntelliCatchUrlReachability } from '../../../models/copilot/intellicatch-reachability.enum';
import { findIntelliCatchLink } from '../../../modules/catch-core/data/constants/app.constants';
import * as UiMessageConstants from '../../../modules/catch-core/data/constants/ui-message.constants';
import { IntelliCatchItemContextService } from '../../../services/intellicatch-item-context.service';

@Component({
  selector: 'app-chat-bot-button',
  templateUrl: './chat-bot-button.component.html',
  styleUrls: ['./chat-bot-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChatBotButtonComponent extends BaseComponent implements OnInit, OnDestroy {
  public chatbotUrl: SafeResourceUrl;
  public chatBotConfigured = false;
  public intellicatchActive = false;
  /** An answer finished while the window was closed; cleared when the window opens. */
  public isResultsReady = false;

  // The resolved iframe URL as a plain string. SafeResourceUrl.toString() never equals '' (it
  // returns the "SafeValue must use [property]=binding" warning), so the enabled check needs a
  // real value to test - without it a blank or unreachable URL still leaves the button clickable
  // and opens an empty iframe.
  private chatbotUrlValue = '';

  private intelliCatchUrl = '';
  private alertedUrl = '';

  private className = 'ChatBotButtonComponent';

  constructor(
    private sanitizer: DomSanitizer,
    private externalLinkStore: ExternalLinkStore,
    private loggerService: LoggerService,
    private alertService: AlertService,
    private intelliCatchReachabilityStore: IntelliCatchReachabilityStore,
    private cdr: ChangeDetectorRef,
    private toolbarEventManagerHelper: ToolbarEventManagerHelper,
    private intelliCatchItemContextService: IntelliCatchItemContextService,
  ) {
    super();
    this.chatbotUrl = this.sanitizer.bypassSecurityTrustResourceUrl('');
  }

  private setChatbotUrl(url: string): void {
    this.chatbotUrlValue = url || '';
    this.chatbotUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.chatbotUrlValue);
  }

  private disableChatbot(): void {
    this.intelliCatchUrl = '';
    this.setChatbotUrl('');
  }

  ngOnInit(): void {
    this.subscribers.resultsReady = this.intelliCatchItemContextService.$resultsReady.subscribe((isReady) => {
      this.isResultsReady = !!isReady;
      this.cdr.markForCheck();
    });

    this.subscribers.intelliCatchReachability = this.intelliCatchReachabilityStore.$reachability.subscribe(() => this.applyReachability());

    this.subscribers.externalLinks = this.externalLinkStore.$firstExternalLink.subscribe((externalLink) => {
      if (externalLink && externalLink.externalLinks) {
        const intelliCatchLink = findIntelliCatchLink(externalLink.externalLinks);

        if (!intelliCatchLink) {
          this.chatBotConfigured = false;
          this.intellicatchActive = false;
          this.loggerService.traceInfo(this.className, 'ngOnInit', 'IntelliCatch link not found in external links', '', null);
          this.disableChatbot();
          this.cdr.detectChanges();
          return;
        }

        // An organisation set to Copilot without Microsoft's admin approval has an IntelliCatch link
        // that looks complete but cannot answer anything - the API refuses those requests rather than
        // silently routing them to a Catch default. Treat it as unconfigured, so the button is
        // disabled and says so, instead of opening a window that fails on its first message.
        if (!isAiProviderUsable(intelliCatchLink.attributes)) {
          this.chatBotConfigured = false;
          this.intellicatchActive = false;
          this.loggerService.traceInfo(this.className, 'ngOnInit', 'IntelliCatch AI provider is not usable for this organisation', '', intelliCatchLink.attributes);
          this.disableChatbot();
          this.cdr.detectChanges();
          return;
        }

        this.chatBotConfigured = true;
        this.intellicatchActive = intelliCatchLink.active === true;

        if (!this.intellicatchActive) {
          this.loggerService.traceInfo(this.className, 'ngOnInit', 'IntelliCatch link is not active', '', intelliCatchLink);
          this.disableChatbot();
          this.cdr.detectChanges();
          return;
        }

        if (intelliCatchLink.url) {
          this.loggerService.traceInfo(this.className, 'ngOnInit', 'Found IntelliCatch URL', '', intelliCatchLink.url);
          this.intelliCatchUrl = intelliCatchLink.url;
          this.applyReachability();
        } else {
          this.loggerService.traceInfo(this.className, 'ngOnInit', 'IntelliCatch URL is empty', '', intelliCatchLink);
          this.disableChatbot();
          this.cdr.detectChanges();
        }
      } else {
        this.chatBotConfigured = false;
        this.intellicatchActive = false;
        this.disableChatbot();
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  // The reachability probe lives in the store because the Generate Tests rule needs the same answer:
  // asking for it here as well would send a second HEAD request for the same URL and let the two
  // surfaces disagree.
  private applyReachability(): void {
    if (!this.intelliCatchUrl) {
      return;
    }

    const reachability = this.intelliCatchReachabilityStore?.reachability ?? IntelliCatchUrlReachability.Unknown;
    if (reachability === IntelliCatchUrlReachability.Reachable) {
      this.loggerService.traceInfo(this.className, 'applyReachability', 'URL validation successful', '', this.intelliCatchUrl);
      this.setChatbotUrl(this.intelliCatchUrl);
    } else {
      if (reachability === IntelliCatchUrlReachability.Unreachable) {
        this.raiseNotAccessibleAlert();
      }
      this.setChatbotUrl('');
    }
    this.cdr.detectChanges();
  }

  // The store re-emits whenever the resolved URL changes, so the alert is raised once for the URL
  // that failed rather than once per emission.
  private raiseNotAccessibleAlert(): void {
    if (!this.intelliCatchUrl || this.alertedUrl === this.intelliCatchUrl) {
      return;
    }
    this.alertedUrl = this.intelliCatchUrl;
    this.alertService.alert(NotificationType.Error, UiMessageConstants.INTELLICATCH_URL_NOT_ACCESSIBLE_ALERT, true);
  }

  get isIntelliCatchFullyEnabled(): boolean {
    return this.chatBotConfigured && this.intellicatchActive && this.chatbotUrlValue !== '';
  }

  public toggleChatbot(): void {
    if (this.isIntelliCatchFullyEnabled) {
      this.toolbarEventManagerHelper.CommonToolbarIntelliCatchWindowOpen = !this.toolbarEventManagerHelper.CommonToolbarIntelliCatchWindowOpen;
    }
  }

  public getTooltipMessage(): string {
    if (this.isIntelliCatchFullyEnabled) {
      return this.isResultsReady ? UiMessageConstants.INTELLICATCH_RESULTS_READY_TOOLTIP : 'Ask IntelliCatch';
    } else if (!this.chatBotConfigured) {
      return ' IntelliCatch is not configured, Please contact "Catch Support" to configure it!';
    } else if (!this.intellicatchActive) {
      return ' IntelliCatch is currently disabled. Please contact "Catch Support" to enable access.';
    } else {
      return 'IntelliCatch URL is not accessible. Please check configuration.';
    }
  }
}
